import { serviceGo } from '@/utils/request'

export interface Chapter {
  id: number
  game_id: number
  title: string
  order: number
  publish_status: number  // 0=草稿, 1=已发布, 2=待审核
  editable: number  // 1=允许编辑, 0=不允许
  visible: number  // 1=对C端用户可见, 0=不可见
  first_published_at: string | null
  /** 未驳回过是 ""，不是 null */
  reject_reason: string
  /** 0 = 从未用新模型保存过。提审要求 > 0（见 submitChapterReview 注释） */
  content_version: number
  /** 最近一次提审时间；驳回不会清空它 */
  submitted_at: string | null
  create_time: string
  update_time: string
}

/** 待审列表专用：在 Chapter 基础上多一个书名 */
export interface PendingChapter extends Chapter {
  game_title: string
}

export function getChapters(gameId: number) {
  return serviceGo.get('/api_v2/chapters', { params: { game_id: gameId } })
}

export function createChapter(gameId: number, title: string) {
  return serviceGo.post('/api_v2/chapters/create', { game_id: gameId, title })
}

export function updateChapter(id: number, title: string, publishStatus: number, editable?: number, visible?: number) {
  const data: Record<string, unknown> = { title, publish_status: publishStatus }
  if (editable !== undefined) {
    data.editable = editable
  }
  if (visible !== undefined) {
    data.visible = visible
  }
  return serviceGo.put(`/api_v2/chapters/${id}`, data)
}

export function deleteChapter(id: number) {
  return serviceGo.delete(`/api_v2/chapters/${id}`)
}

/**
 * 提审。**失败分支几乎全是 HTTP 200 + code !== 0，不会进 catch**：
 * - 6010「章节已在待审核」
 * - 1001「已发布章节请先取消发布再提审」
 * - 1001「章节尚未保存，不能提审」（content_version <= 0；前端应提前禁用按钮）
 * - 6001「本章存在悬空跳转，请先修复」，**带 data.errors[]，要逐条展示**
 *   （每条形如 { rule: 'dangling_target', path: 'nodes[<节点id>].<字段>', message: '跳转目标 <id> 不存在' }）
 * - 1001「章节状态已变化，请刷新」（并发竞态）
 * HTTP 403 有两类：①「作者 + 章节已锁定」（code 6010）；②调用者对该章节所属书无访问权限
 * （code 1004「无权操作该书」，任意角色都可能触发，在上面的状态判断之前就会拦下）。
 * 两者都会被全局拦截器自动弹窗。
 * 成功后：publish_status=2, editable=0, submitted_at=now, reject_reason=""
 */
export function submitChapterReview(id: number) {
  return serviceGo.post(`/api_v2/chapters/${id}/submit-review`)
}

/**
 * 审核通过。**仅 editor / admin**，作者调用返 403 + 1004；
 * 编辑对非管辖书也是 403 + 1004「无权操作该书」（书级鉴权，在状态判断之前）。
 * 非待审核态 → 200 + 1001「章节不在待审核状态」。
 * 成功后：publish_status=1，**editable 保持不变（不主动解锁）**，first_published_at 首次置值。
 */
export function approveChapter(id: number) {
  return serviceGo.post(`/api_v2/chapters/${id}/approve`)
}

/**
 * 驳回。**仅 editor / admin**（书级鉴权同 approveChapter，非管辖书 403 + 1004）。
 * reason 必填、**按码点计 ≤500**（后端用
 * utf8.RuneCountInString，不是字节；前端预检要用 Array.from(s).length）。
 * 成功后：publish_status=0, editable=1, reject_reason=trim 后的值，
 * **submitted_at 保留不清空**。
 */
export function rejectChapter(id: number, reason: string) {
  return serviceGo.post(`/api_v2/chapters/${id}/reject`, { reason })
}

/**
 * 待审列表。**仅 editor / admin**。gameId 可选：
 * - admin 不传 = 全部书；editor 不传 = 自己管辖的书
 * - editor 传了非管辖书的 id → 403 + 1004「无权查看该书」
 * 成功 data 是 `{ chapters: PendingChapter[] }`。
 * **后端无分页**，返回全量；已按 submitted_at ASC, id ASC 排序（最早提审的排最前），前端不要重排。
 */
export function getPendingReviewChapters(gameId?: number) {
  return serviceGo.get('/api_v2/chapters/pending-review', {
    params: gameId ? { game_id: gameId } : {}
  })
}
